import React, { useState } from "react";
import { Formik, yupToFormErrors } from "formik";
import Checkbox from "components/shared/Checkbox";
import Select from "components/shared/Select";
import LcFetch from "lib/shared/fetch";
import * as Yup from "yup";
import MaskedTextField from "components/payments/fields/MaskedTextField";
import PaymentError from "components/payments/PaymentError";
import { buildSignupParams, logAccountCreated } from "components/payments/util";

const emptyValues = {
  birthday: "",
  country_code: "",
  email_marketing_optin: false
};

const validationSchema = Yup.object().shape({
  birthday: Yup.string().min(
    8,
    "Please enter your birthday in the MM/DD/YYYY format"
  ),
  country_code: Yup.string().required("Please select your country")
});

export function GdprUserForm(props) {
  const {
    countries,
    countryCode,
    isGdprCountry,
    dispatch,
    validateUserPath,
    subscriptionType,
    requests
  } = props;
  const [isGdprCompliant, setIsGdrCompliant] = useState(isGdprCountry);
  const [errorMessage, setErrorMessage] = useState();
  const autoOptInEmailMarketing = countryCode && !isGdprCountry;
  const initialValues = {
    ...emptyValues,
    country_code: countryCode,
    email_marketing_optin: autoOptInEmailMarketing
  };

  const validateUserForm = async (values) => {
    // Doing a frontend validation before we hit the server to save a round-trip
    let yupFormErrors = {};
    try {
      await validationSchema.validate(values, { abortEarly: false });
    } catch (e) {
      yupFormErrors = yupToFormErrors(e);
    }

    // Validate against server requirements
    const result = await requests.validateUser(validateUserPath, values);
    const data = await result.json();
    if (!data.valid) {
      const serverErrors = data.errors.reduce(
        (acc, [k, v]) => ({ ...acc, [k]: v[0] }),
        {}
      );
      return { ...serverErrors, ...yupFormErrors };
    }
    return yupFormErrors;
  };

  const onSubmit = (values) => {
    setErrorMessage(null);
    const updatedValues = { ...values, subscription: subscriptionType };
    requests.createUser(updatedValues).then((response) => {
      response.json().then((data) => {
        if (response.ok) {
          logAccountCreated(data.user_analytics);
          dispatch({
            clientSecret: data.stripe_client_secret,
            type: "payment_step"
          });
        } else {
          setErrorMessage(data.errors.base[0]);
        }
      });
    });
  };
  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      validate={validateUserForm}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} data-testid="gdpr-user-form">
          {!!errorMessage && <PaymentError errorMessage={errorMessage} />}
          {!countryCode && (
            <Select
              baseClassName="lc-select-container--md lc-field"
              name="user_country_code"
              value={formik.values.country_code}
              id="user_country_code"
              placeholder="Select one"
              options={countries}
              labelText="Country"
              errorMessage={formik.errors.country_code}
              onChange={(e) => {
                setIsGdrCompliant(e.conforms_to_gdpr);
                formik.setFieldValue("country_code", e.value);
              }}
              onBlur={formik.handleBlur}
            />
          )}
          <MaskedTextField
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.birthday}
            name="birthday"
            placeholder="MM / DD / YYYY"
            text="Birthday"
            errorMessage={formik.errors.birthday}
          />
          {isGdprCompliant && (
            <Checkbox
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email_marketing_optin}
              name="email_marketing_optin"
              label="Get email updates when we publish new guides and add new features. You can unsubscribe at any time. (optional)" // eslint-disable-line max-len
              checked={formik.values.email_marketing_optin}
              containerClass="user_marketing_optin--condensed-form"
            />
          )}
          <button
            type="submit"
            className="form-button form-submit btn btn-a-plus btn-block"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? "Submitting..." : "Continue to Payment"}
          </button>
        </form>
      )}
    </Formik>
  );
}

const requests = {
  createUser: (values) =>
    LcFetch("/", buildSignupParams(values, values.subscription)),
  validateUser: (validateUserPath, values) =>
    LcFetch(validateUserPath, buildSignupParams(values))
};

export default function (props) {
  return <GdprUserForm {...props} requests={requests} />;
}
